import React, { useState, useMemo } from 'react';
import { formatDateTime } from 'v3/utils/formatter';
import Lightbox from 'components/Lightbox';
import { FaSearch, FaDownload } from 'react-icons/fa';
import PDFView from 'v3/components/PDFView';
import StringBuilder from 'utils/StringBuilder';
import Modal from 'v3/components/Modal';
import Text from 'v3/components/Text';
import * as Styled from '../styles';

export default function Attachment({ attachment, attachments }) {
  const [isHovering, setHovering] = useState(false);
  const [isShowingModal, setShowingModal] = useState(false);
  const attachmentsWithoutPDF = useMemo(() => {
    return attachments?.filter((item) => {
      const filterPDF = item.name?.split('.');
      if (filterPDF[filterPDF?.length - 1] === 'pdf') return null;
      return item;
    });
  }, [attachments]);

  const [lightbox, setLightbox] = useState({
    isOpen: false,
    attachments: attachmentsWithoutPDF,
    index: 0,
  });

  const lightboxIndexes = useMemo(() => {
    const next = (lightbox.index + 1) % lightbox.attachments?.length;
    const previous =
      (lightbox.index + lightbox.attachments.length - 1) % lightbox.attachments.length;
    return { next, previous };
  }, [lightbox.index, lightbox.attachments]);

  function onClickPreview(file) {
    const index = file ? attachmentsWithoutPDF.findIndex((a) => a === file) : 0;
    setLightbox((prev) => ({
      ...prev,
      index,
      attachments: attachmentsWithoutPDF,
      isOpen: true,
    }));
  }

  function handleClick() {
    const fullPathArray = attachment.name?.split('.');

    if (fullPathArray?.[fullPathArray?.length - 1] === 'pdf') {
      setShowingModal(true);
    } else {
      onClickPreview(attachment);
    }
  }

  function getImagePath(index, alwaysDisplay) {
    const isSameAsIndex =
      lightbox.index === lightboxIndexes.next && lightbox.index === lightboxIndexes.previous;

    if (!alwaysDisplay && isSameAsIndex && lightbox.attachments.length === 1) {
      return null;
    }
    return lightbox.attachments[index]?.full_path;
  }

  function getDocumentMetadata() {
    const document = lightbox.attachments[lightbox.index];
    const type = '';
    const createdAt = formatDateTime(document?.created_at);

    return { type, createdAt };
  }

  const lightboxTitle = useMemo(() => {
    const builder = new StringBuilder(' ');
    const documentMetadata = getDocumentMetadata();
    builder
      .append(`${lightbox.index + 1}/${lightbox.attachments?.length}`)
      .append('|')
      .append(documentMetadata.type)
      .append(`(${documentMetadata.createdAt})`);

    return builder.build();
  }, [lightbox.index, lightbox.attachments]);

  return (
    <div className="px-4">
      <Modal
        show={isShowingModal}
        dialogClassName="modal-90w"
        onHide={() => setShowingModal(false)}
        heading="Visualizar PDF"
        body={<PDFView file={attachment.full_path} />}
      />

      <div
        className="position-relative"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        style={{
          width: 150,
          height: 85,
          background: `url(${
            attachment.name?.split('.')?.pop() === 'pdf'
              ? '/images/default-doc.png'
              : attachment.full_path
          }) center center / cover`,
        }}
      >
        {lightbox.isOpen && (
          <Lightbox
            mainSrc={getImagePath(lightbox.index, true)}
            nextSrc={getImagePath(lightboxIndexes.next)}
            prevSrc={getImagePath(lightboxIndexes.previous)}
            onCloseRequest={() => setLightbox((prev) => ({ ...prev, isOpen: false }))}
            onMovePrevRequest={() =>
              setLightbox((prev) => ({
                ...prev,
                index: lightboxIndexes.previous,
              }))
            }
            onMoveNextRequest={() =>
              setLightbox((prev) => ({
                ...prev,
                index: lightboxIndexes.next,
              }))
            }
            imageTitle={lightboxTitle}
          />
        )}
        {isHovering && (
          <Styled.Backdrop>
            <button type="button" onClick={handleClick}>
              <FaSearch color="#fff" size={28} />
            </button>
            <a href={attachment.full_path} download>
              <FaDownload color="#fff" size={32} />
            </a>
          </Styled.Backdrop>
        )}

        <Styled.DateTimeSm>
          <Text type="label" color="white" fontSize={9}>
            {formatDateTime(attachment.created_at)}
          </Text>
        </Styled.DateTimeSm>
      </div>
    </div>
  );
}
